import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import CommentTemplateCard from './components/CommentTemplateCard';
import CommentQueueItem from './components/CommentQueueItem';
import TargetingFilters from './components/TargetingFilters';
import PerformanceMetrics from './components/PerformanceMetrics';
import BrandSafetyPanel from './components/BrandSafetyPanel';
import AICommentConfig from './components/AICommentConfig';
import AICommentPreview from './components/AICommentPreview';
import Icon from '../../components/AppIcon';



import { commentService } from '../../services/commentService';


const CommentAutomation = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [showAISection, setShowAISection] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Real data states
  const [templates, setTemplates] = useState([]);
  const [queueItems, setQueueItems] = useState([]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'templates') {
        const templatesData = await commentService?.getTemplates();
        setTemplates(templatesData);
      } else if (activeTab === 'queue') {
        const queueData = await commentService?.getQueueItems();
        setQueueItems(queueData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const [blacklist, setBlacklist] = useState([
    "politics",
    "religion",
    "controversial",
    "spam",
    "offensive"
  ]);

  const [safetySettings, setSafetySettings] = useState({
    avoidControversial: true,
    sentimentAnalysis: true,
    competitorMonitoring: false
  });

  const platformOptions = [
    { value: 'all', label: 'All Platforms' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'linkedin', label: 'LinkedIn' }
  ];

  const sortOptions = [
    { value: 'score', label: 'Opportunity Score' },
    { value: 'followers', label: 'Follower Count' },
    { value: 'engagement', label: 'Engagement Rate' },
    { value: 'recent', label: 'Recently Active' }
  ];

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleDeleteTemplate = async (template) => {
    try {
      await commentService?.deleteTemplate(template?.id);
      await loadData();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err?.message || 'Failed to delete template');
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setActiveTab('queue');
  };

  const handleEditQueueItem = async (item) => {
    console.log('Editing queue item:', item);
  };

  const handleDeleteQueueItem = async (item) => {
    try {
      await commentService?.deleteQueueItem(item?.id);
      await loadData();
    } catch (err) {
      console.error('Error deleting queue item:', err);
      setError(err?.message || 'Failed to delete queue item');
    }
  };

  const handleRescheduleQueueItem = async (item) => {
    console.log('Rescheduling queue item:', item);
  };

  const handleApplyFilters = (filters) => {
    console.log('Applying filters:', filters);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    console.log('Resetting filters');
  };

  const handleAddToBlacklist = (keyword) => {
    setBlacklist([...blacklist, keyword]);
  };

  const handleRemoveFromBlacklist = (keyword) => {
    setBlacklist(blacklist?.filter((k) => k !== keyword));
  };

  const handleAIConfigSave = (config) => {
    setAiConfig(config);
    console.log('AI Configuration saved:', config);
  };

  const mockMetrics = {
    commentsPosted: queueItems?.filter(i => i?.status === 'completed')?.length || 0,
    commentsPostedChange: 12,
    profileVisits: 0,
    profileVisitsChange: 18,
    newFollowers: 0,
    newFollowersChange: 24,
    responseRate: templates?.reduce((acc, t) => acc + t?.successRate, 0) / (templates?.length || 1) || 0,
    responseRateChange: -3
  };

  const tabs = [
    { id: 'search', label: 'Target Search', icon: 'Search' },
    { id: 'templates', label: 'Templates', icon: 'FileText' },
    { id: 'queue', label: 'Comment Queue', icon: 'Clock' },
    { id: 'safety', label: 'Brand Safety', icon: 'Shield' }
  ];

  if (loading && templates?.length === 0 && queueItems?.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* AI Automation Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-foreground">AI-Powered Automation</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Let AI automatically comment on posts matching your keywords
                </p>
              </div>
              <button
                onClick={() => setShowAISection(!showAISection)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {showAISection ? 'Hide' : 'Show'} AI Settings
              </button>
            </div>

            {showAISection && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AICommentConfig onConfigSave={handleAIConfigSave} />
                {aiConfig && <AICommentPreview config={aiConfig} />}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 mb-8"></div>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Comment Automation</h1>
              <p className="text-muted-foreground">Strategic comment placement for organic growth</p>
            </div>
            <Button
              variant="default"
              iconName="BarChart3"
              iconPosition="left"
              onClick={() => navigate('/analytics-dashboard')}>
              View Analytics
            </Button>
          </div>

          <PerformanceMetrics metrics={mockMetrics} />

          <div className="mt-8 bg-card border border-border rounded-lg">
            <div className="border-b border-border">
              <div className="flex items-center gap-1 p-2 overflow-x-auto">
                {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}>
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'search' &&
                <div className="space-y-6">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1">
                      <Input
                        type="search"
                        placeholder="Search users by name, username, or keywords..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Select
                        options={platformOptions}
                        value="all"
                        onChange={() => {}}
                        className="w-full lg:w-48"
                      />
                      <Select
                        options={sortOptions}
                        value="score"
                        onChange={() => {}}
                        className="w-full lg:w-48"
                      />
                      <Button
                        variant="outline"
                        iconName="Filter"
                        onClick={() => setShowFilters(!showFilters)}>
                        Filters
                      </Button>
                    </div>
                  </div>

                  {showFilters && (
                    <TargetingFilters
                      onApplyFilters={handleApplyFilters}
                      onResetFilters={handleResetFilters}
                    />
                  )}

                  <div className="text-center py-12">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Target Search Coming Soon</h3>
                    <p className="text-muted-foreground">
                      This feature will help you find ideal users to engage with based on your criteria
                    </p>
                  </div>
                </div>
              }

              {activeTab === 'templates' &&
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {templates?.length} templates available
                    </p>
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left">
                      Create Template
                    </Button>
                  </div>

                  {templates?.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {templates?.map((template) =>
                        <CommentTemplateCard
                          key={template?.id}
                          template={template}
                          onEdit={handleEditTemplate}
                          onDelete={handleDeleteTemplate}
                          onUse={handleUseTemplate}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Templates Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create your first comment template to get started
                      </p>
                      <Button variant="default" iconName="Plus" iconPosition="left">
                        Create Template
                      </Button>
                    </div>
                  )}
                </div>
              }

              {activeTab === 'queue' &&
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {queueItems?.length} comments scheduled
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        iconName="Pause"
                        iconPosition="left">
                        Pause All
                      </Button>
                      <Button
                        variant="default"
                        iconName="Plus"
                        iconPosition="left">
                        Add Comment
                      </Button>
                    </div>
                  </div>

                  {queueItems?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                      {queueItems?.map((item) =>
                        <CommentQueueItem
                          key={item?.id}
                          item={item}
                          onEdit={handleEditQueueItem}
                          onDelete={handleDeleteQueueItem}
                          onReschedule={handleRescheduleQueueItem}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Comments Scheduled</h3>
                      <p className="text-muted-foreground mb-4">
                        Add comments to your queue to start automating engagement
                      </p>
                      <Button variant="default" iconName="Plus" iconPosition="left">
                        Add Comment
                      </Button>
                    </div>
                  )}
                </div>
              }

              {activeTab === 'safety' &&
                <BrandSafetyPanel
                  blacklist={blacklist}
                  onAddToBlacklist={handleAddToBlacklist}
                  onRemoveFromBlacklist={handleRemoveFromBlacklist}
                  safetySettings={safetySettings}
                  onUpdateSettings={setSafetySettings}
                />
              }
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CommentAutomation;